/* eslint-disable no-console */
import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormContextProvider } from '@sw-sw/lib-form';
import {
  faEllipsisV,
  faExternalLinkAlt,
  faSort,
  faCalendarAlt,
  faList,
  faStickyNote,
  faTrash,
  faDownload,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { Link } from 'react-router-dom';
import Loading from '../../../../components/Shared/ResourceIndex/Loading';
import CalendarView from './CalendarView';
import classNames from 'classnames';
import DashboardContext from '../../../../contexts/DashboardContext';
import EllipsisPopover from '../../shared/EllipsisPopover/EllipsisPopover';
import ProjectList from './ProjectList';
import userApi from '../../../../utils/api/user';
import AppContext from '../../../../contexts/AppContext';
import ExternalProjectsInspectionSubmitModal from '../../../Inspections/Listing/ExternalProjectsInspectionSubmitModal';
import xhrService from '../../../../utils/xhrService';
import authorizationAgreementApi from '../../../../utils/api/authorizationAgreement';
import inspectionApi from '../../../../utils/api/inspection';
import offlineApi from '../../../../utils/api/offline';
import mapApi from '../../../../utils/api/map';
import DirectionModal from './DirectionModal';
import ConfirmationModal from '../../../Shared/ConfirmationModal/ConfirmationModal';
import findingApi from '../../../../utils/api/finding';
import clientApi from '../../../../utils/api/client';
import { inspectionQuestionApi } from '../../../../utils/api/inspectionQuestion';
import { certificationApi } from '../../../../utils/api/certification';
import commentableApi from '../../../../utils/api/commentable';
import { getDownloadUrl } from '../../../../utils/api/upload';
import projectInspectionApi from '../../../../utils/api/projectInspection';
import { questionTypeApi } from '../../../../utils/api/questionType';
import { templateQuestionConfigApi } from '../../../../utils/api/templateQuestionConfig';
import { findingTypesApi } from '../../../../utils/api/findingTypes';
import { inspectionTemplateApi } from '../../../../utils/api/inspectionTemplate';
import { questionConfigValueApi } from '../../../../utils/api/questionConfigValue';
import { projectApi } from '../../../../utils/api/project';
import { uploadApi } from '../../../../utils/api/upload';
import { signatureEditorApi } from '../../../../utils/api/signatureEditor';
import { positionableApi } from '../../../../utils/api/positionable';
import { templatePriorityOptionsApi } from '../../../../utils/api/templatePriorityOptionsApi'
import db, { removeOfflineProjects as removeOfflineProject, downloadAndStoreImage } from '../../../../db/db';
import legendItemApi from '../../../../utils/api/legendItem';
import { uniq } from 'lodash';
import { LinearProgress, Typography, Paper } from '@mui/material';
import { toast } from 'react-toastify';
import { useOffline } from '../../../../contexts/OfflineContext'
import { AppDivisionContext } from "../../../../contexts/AppDivisionContext";
import permitApi from '../../../../utils/api/permit';
import TenantFeatureContext from '../../../../contexts/TenantContext';
import { isValidLatLong } from '../../../../utils/googlemaps';
import { format } from 'date-fns';
import schedulerApi from '../../../../utils/api/inspectionSchedular';
import { useTenantRedirect } from '../../../../hooks/useTenantRedirect';
import MFAVerificationModal from '../../../Shared/MFAVerificationModal';
function getCtaRoute(inspectionStatus, dueDate, projectId, inspectionId = null) {
  if (
    inspectionStatus === 'scheduled' &&
    moment().format('MM-DD-YYYY') === dueDate &&
    navigator.onLine
  ) {
    return `/projects/${projectId}/start-inspection`;
  }

  if (inspectionId > 0 && dueDate) {
    const today = new Date();
    const due = parseDueDateLocal(dueDate);

    today.setHours(0, 0, 0, 0);
    if (today > due) {
      return `/inspection/${inspectionId}/questionnaire`;
    }
  }

  return `/projects/${projectId}/inspections`;
}

const fetchInspectionTemplateData = async (templateId) => {

  const [findingTypes, templateQuestionConfig, priority] = await Promise.all([
    findingTypesApi.index(Number(templateId)),
    templateQuestionConfigApi.index(templateId),
    templatePriorityOptionsApi.getByTemplateId(templateId)
  ]);

  await db.findingTypeInspectionTemplate
    .put({
      templateId: templateId,
      findingTypes: findingTypes,
    })
    .catch((err) => {
      console.error('Error storing finding types:', err);
    });
}

const downloadInspectionTemplateData = async (templateId) => {

  const questionsData = await inspectionQuestionApi.getDefaultAnswers(
    templateId,
  );

  // Process condition questions
  if (questionsData.question_groups) {
    const conditionGroup = questionsData.question_groups.find(
      (group) => group.name === 'Condition Group',
    );

    if (conditionGroup) {
      const conditionQuestions = conditionGroup.questions.filter(
        (question) => question.input_label === 'Condition',
      );

      for (const question of conditionQuestions) {
        await questionConfigValueApi.index(question.id);
      }
    }
  }

  return questionsData.question_groups;
};

// Parses MM-DD-YYYY to local midnight, avoiding UTC offset shifting the date in US timezones
function parseDueDateLocal(dueDate) {
  const [month, day, year] = dueDate.split('-');

  return new Date(Number(year), Number(month) - 1, Number(day));
}

function getCtaText(dueDate, inspectionStatus) {
  if (!dueDate) {
    return 'View Project';
  }

  const formattedTodayDate = new Date();
  const formattedDueDate = parseDueDateLocal(dueDate);

  if (
    formattedTodayDate.setHours(0, 0, 0, 0) === formattedDueDate.getTime() &&
    (inspectionStatus === 'scheduled' || inspectionStatus === 'new')
  ) {
    return 'Start Inspection';
  }

  if (
    formattedTodayDate.setHours(0, 0, 0, 0) === formattedDueDate.getTime() &&
    (inspectionStatus === 'started' || inspectionStatus === 'answered')
  ) {
    return 'Finish';
  }

  if (formattedTodayDate > formattedDueDate) {
    return 'Past Due';
  }

  return 'View Project';
}

/* "My Projects" widget was formerly titled as "Inspections Due".
 * In case, looking for naming convention to make sense,
 * think of "Inspections Due" as the main widget with "My Project/Project List" as a part of it.
 */
const InspectionsDue = ({ providedProps, changeShowMore, mdSize }) => {
  const [search, setSearch] = useState('');
  const [clientIsAscending, setClientIsAscending] = useState(true);
  const [siteIsAscending, setSiteIsAscending] = useState(true);
  const [startingInspectionKey, setStartingInspectionKey] = useState(null);
  const {
    displayStatsForID,
    isLoadingForID,
    allProjectsForID,
    setDisplayStatsForID,
    allProjectsForMP,
    setDisplayStatsForMP,
    fetchStatsFuncForID,
    getTeamManagementData,
  } = useContext(DashboardContext);
  const widgetRef = useRef(0);
  const [widgetWidth, setWidgetWidth] = useState(widgetRef.current.clientWidth);
  const appContext = useContext(AppContext);
  const [
    showExternalProjectInspectionModal,
    setShowExternalProjectInspectionModal,
  ] = useState(false);
  const [modalInspection, setModalInspection] = useState({});
  const [inspectionTypes, setInspectionTypes] = useState({});
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [openDirectionModal, setOpenDirectionModal] = useState(false);
  const [showRemoveOfflineDownloadModal, setShowRemoveOfflineDownloadModal] = useState(false);
  const [offlineProjectId, setOfflineProjectId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [offlineProjects, setOfflineProjects] = useState([]);
  const steps = [
    'Downloading project...',
    'Downloading inspection...',
    'Downloading images...',
    'Downloading assets...',
    'Downloading data...',
    'Processing files...',
    'Verifying integrity...',
    'Finalizing setup...',
    'Completing download...',
    'Download complete!',
  ];
  const [statusText, setStatusText] = useState('Initializing...');

  const [syncProgress, setSyncProgress] = useState(0);
  const [syncStatus, setSyncStatus] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const isOffline = useOffline();
  const appDivisionContext = useContext(AppDivisionContext);
  const tenantFeatureContext = useContext(TenantFeatureContext);
  const {
    redirectToTenant,
    showMFAModal,
    mfaData,
    handleMFAVerificationSuccess,
    handleMFAModalClose,
    authStore,
  } = useTenantRedirect();

  const user = appContext.get('user');

  let viewName = 'Inspections Due';

  let viewIcon = faCalendarAlt;
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  if (localStorage.getItem('myProjectsWidgetPreference') === 'InspectionsDue') {
    viewIcon = faCalendarAlt;
    viewName = 'Insp. Calendar';
  }

  if (localStorage.getItem('myProjectsWidgetPreference') === 'Insp. Calendar') {
    viewIcon = faStickyNote;
    viewName = 'Inspections Due';
  }

  useEffect(() => {
    function handleResize() {
      setWidgetWidth(widgetRef.current.clientWidth);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeView = () => {
    setSelectedProjects([]);
    if (
      localStorage.getItem('myProjectsWidgetPreference') === 'InspectionsDue'
    ) {
      localStorage.setItem('myProjectsWidgetPreference', 'Insp. Calendar');
    } else {
      localStorage.setItem('myProjectsWidgetPreference', 'InspectionsDue');
    }
    setAnchorEl(null);
  };

  const handleProjectListView = () => {
    setSelectedProjects([]);
    localStorage.setItem('myProjectsWidgetPreference', 'ProjectList');
    setAnchorEl(null);
  };

  const handleButtonColors = (dueDate, inspectionStatus) => {
    if (getCtaText(dueDate, inspectionStatus) === 'View Project')
      return 'list-view-show-project-color';

    if (getCtaText(dueDate, inspectionStatus) === 'Past Due')
      return 'list-view-past-due-color';

    if (getCtaText(dueDate, inspectionStatus) === 'Finish')
      return 'list-view-finish-project-color';

    return 'list-view-start-inspection-color';
  };

  const handleStartInspection = async (ele) => {
    try {
      if (!ele.projectId) {
        console.error('Project ID is required to start inspection');
        toast.error('Project ID is missing');
        return;
      }

      // Use project_inspection_template_id as the junction ID
      const junctionId = ele.project_inspection_template_id || ele.junctionId;

      if (!junctionId) {
        console.error('Junction ID (project_inspection_template_id) is required to start inspection');
        toast.error('Junction ID is missing');
        return;
      }

      // Map inspection type to ensure it matches API expectations
      const validInspectionTypes = [
        'Routine',
        'Routine + Post Storm',
        'Post Storm',
        'Final',
        'Other',
        'Pre-Qualifying Precipitation Event',
        'During Qualifying Precipitation Event',
        'Post-Qualifying Precipitation Event',
        'Before Release of Contained Storm Water',
        'During Release of Contained Storm Water',
        'Quarterly Non-Storm Water'
      ];

      let inspectionType = ele.inspection_type || ele.inspectionType || 'Routine';

      // If the inspection type is not in the valid list, default to 'Routine'
      if (!validInspectionTypes.includes(inspectionType)) {
        console.warn(`Invalid inspection type "${inspectionType}", defaulting to "Routine"`);
        inspectionType = 'Routine';
      }

      const requestBody = {
        junctionId: junctionId,
        projectId: ele.projectId,
        inspection_types: [inspectionType]
      };

      // Use the inspection API with the createWithJunction method
      const data = await inspectionApi.createWithJunction(requestBody);

      if (data && data.id) {
        // Navigate to the newly created inspection's questionnaire
        window.location.href = `/inspection/${data.id}/questionnaire`;

        // Optionally refetch data
        getTeamManagementData.refetch();
        fetchStatsFuncForID.refetch();
      } else {
        console.error('Failed to create inspection: No inspection ID returned');
        toast.error('Failed to start inspection. Please try again.');
      }
    } catch (error) {
      console.error('Error starting inspection:', error);
      if (error?.response?.status === 403 && error?.response?.data?.error?.includes('Authorization & Agreement')) {
        toast.error('The Authorization & Agreement must be signed by both parties before inspections can be started.');
      } else {
        toast.error('Failed to start inspection. Please try again.');
      }
    }
  };

  const handleCalendarStartInspection = async (ele) => {
    const { projectId, dueDate, division } = ele;

    try {
      appDivisionContext.saveAppDivisionId(division.id);

      const response = await schedulerApi.getUpcomingInspections(projectId);

      if (!response.success || !response.data) {
        toast.error('Failed to fetch inspection details. Please try again.');
        return;
      }

      // dueDate is MM-DD-YYYY — convert to local midnight for comparison
      const [month, day, year] = dueDate.split('-');
      const dueMidnight = new Date(Number(year), Number(month) - 1, Number(day));

      dueMidnight.setHours(0, 0, 0, 0);

      const isDateMatch = (ins) => {
        const d = new Date(ins.date_of_inspection);
        const insMidnight = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

        insMidnight.setHours(0, 0, 0, 0);

        return (
          insMidnight.getTime() === dueMidnight.getTime() &&
          ins.status !== 'complete' &&
          ins.status !== 'cancelled'
        );
      };

      // Prefer scheduled_inspection records — their id is a project_inspection_templates.id (valid junctionId).
      // Existing-inspection records (is_existing && !scheduled_inspection) carry an inspections.id which is not a valid junctionId.
      const todayInspection =
        response.data.find((ins) => isDateMatch(ins) && ins.scheduled_inspection) ||
        response.data.find((ins) => isDateMatch(ins));

      if (!todayInspection || !todayInspection.id) {
        toast.error('No scheduled inspection found for today. Please refresh and try again.');

        return;
      }

      const validInspectionTypes = [
        'Routine', 'Routine + Post Storm', 'Post Storm', 'Final', 'Other',
        'Pre-Qualifying Precipitation Event', 'During Qualifying Precipitation Event',
        'Post-Qualifying Precipitation Event', 'Before Release of Contained Storm Water',
        'During Release of Contained Storm Water', 'Quarterly Non-Storm Water',
      ];

      let inspectionType = ele.inspection_type || ele.inspectionType || todayInspection.inspection_type || 'Routine';

      if (!validInspectionTypes.includes(inspectionType)) {
        inspectionType = 'Routine';
      }

      const requestBody = {
        junctionId: todayInspection.id,
        projectId,
        inspection_types: [inspectionType],
      };

      const data = await inspectionApi.createWithJunction(requestBody);

      if (data && data.id) {
        getTeamManagementData.refetch();
        fetchStatsFuncForID.refetch();
        window.location.href = `/inspection/${data.id}/questionnaire`;
      } else {
        toast.error('Failed to start inspection. Please try again.');
      }
    } catch (error) {
      console.error('Error starting inspection from calendar:', error);
      toast.error('Failed to start inspection. Please try again.');
    }
  };

  const handleEPVInspShowModal = (ele, startEvpInspection = false) => {
    if (startEvpInspection) {
      inspectionApi.create(ele.projectId, 'Routine', 'External Project View').then((i) => {
        const modalData = {
          inspectionId: i.id,
          siteName: i.project.name,
          ...i,
        };

        getTeamManagementData.refetch();
        fetchStatsFuncForID.refetch();
        setModalInspection(modalData);
        setShowExternalProjectInspectionModal(true);
      });
    } else {
      setModalInspection(ele);
      setShowExternalProjectInspectionModal(true);
    }
  };

  const handleEPVInspSubmit = async (formData) => {
    appContext.loadData(
      async () =>
        await xhrService
          .put(
            `/api/inspections/${modalInspection.inspectionId}/update-evp-inspection`,
            {
              ...formData,
            },
          )
          .then(() => {
            setShowExternalProjectInspectionModal(false);
            setModalInspection({});
            fetchStatsFuncForID.refetch();
          }),
    );
  };

  const getInspectionTypes = async () => {
    const dataTypes = await xhrService.get(`/api/inspection-types`);
    const mappedData = dataTypes.data.map((e) => ({ type: e }));
    setInspectionTypes(mappedData);
  };

  //SEARCH
  useEffect(() => {
    setSelectedProjects([]);
    if (
      localStorage.getItem('myProjectsWidgetPreference') === 'InspectionsDue' ||
      localStorage.getItem('myProjectsWidgetPreference') === 'Insp. Calendar'
    ) {
      const updatedList =
        allProjectsForID &&
        [...allProjectsForID].filter((el) => {
          return el.siteName.toLowerCase().includes(search.toLowerCase());
        });

      setDisplayStatsForID(updatedList);
    } else {
      const updatedList = [...allProjectsForMP].filter((el) => {
        return (
          el.project.name.toLowerCase().includes(search.toLowerCase()) ||
          el.project.client.name.toLowerCase().includes(search.toLowerCase())
        );
      });

      setDisplayStatsForMP(updatedList);
    }
  }, [search]);


  const setOfflineProjectsFunc = async () => {

    const projectsDownloadedByUser = await offlineApi.getOfflineByUser(user.id)

    const offlineProjectsData = await db.projects.toArray();

    const offlineProject = offlineProjectsData.filter((project) => {
      return projectsDownloadedByUser.projects.some(
        (offlineProject) => offlineProject.project_id === project.id,
      );
    });

    const offlineProjectDeleted = offlineProjectsData.filter((project => {
      return !projectsDownloadedByUser.projects.some(
        (offlineProject) => offlineProject.project_id === project.id,
      );
    }));

    for (const project of offlineProjectDeleted) {
      await removeOfflineProject(project.id);
    }


    if (offlineProject.length === 0) {
      setOfflineProjects([]);
      if (isOffline)
        setDisplayStatsForID([])
      return;
    }

    setOfflineProjects(offlineProject);
  };


  useEffect(() => {

    if (isOffline && displayStatsForID && displayStatsForID.length && offlineProjects.length) {
      setDisplayStatsForID((prev) => {
        return prev.filter((item) => {
          return offlineProjects.some((offlineProj) => offlineProj.id === item.projectId);
        });
      })
    }

  }, [isOffline, offlineProjects])

  useEffect(() => {

    setOfflineProjectsFunc();

    getInspectionTypes();
    if (localStorage.getItem('myProjectsWidgetPreference') === null) {
      localStorage.setItem('myProjectsWidgetPreference', 'InspectionsDue');
    }
  }, []);

  //SORT
  const sortBySiteName = () => {
    const updatedList = displayStatsForID.sort((a, b) => {
      let A = a.siteName.toString().toLowerCase();

      let B = b.siteName.toString().toLowerCase();

      if (siteIsAscending === true) {
        setSiteIsAscending(!siteIsAscending);

        return A > B ? -1 : 1; // descending
      } else {
        setSiteIsAscending(!siteIsAscending);

        return A > B ? 1 : -1; //ascending
      }
    });

    setDisplayStatsForID(updatedList);
  };

  const sortByClientName = () => {
    const updatedList = displayStatsForID.sort((a, b) => {
      let A = a.clientName.toString().toLowerCase();

      let B = b.clientName.toString().toLowerCase();

      if (clientIsAscending === true) {
        setClientIsAscending(!clientIsAscending);

        return A > B ? -1 : 1; // descending
      } else {
        setClientIsAscending(!clientIsAscending);

        return A > B ? 1 : -1; //ascending
      }
    });

    setDisplayStatsForID(updatedList);
  };

  const sortByDueDate = () => {
    const updatedList = displayStatsForID.sort((a, b) => {
      let A = a.dueDate ? parseDueDateLocal(a.dueDate) : new Date(0);

      let B = b.dueDate ? parseDueDateLocal(b.dueDate) : new Date(0);

      if (clientIsAscending === true) {
        setClientIsAscending(!clientIsAscending);

        return A > B ? -1 : 1; // descending
      } else {
        setClientIsAscending(!clientIsAscending);

        return A > B ? 1 : -1; //ascending
      }
    });

    setDisplayStatsForID(updatedList);
  };

  let oneColumnHeader = {
    // gridTemplateAreas: '"title title title . bars bars bars bars bars bars bars ellipsis"'
  };

  const handleRedirect = (ele) => {
    const redirectPath = `/divisions/${ele.division.id}${getCtaRoute(
      ele.inspectionStatus,
      ele.dueDate,
      ele.projectId,
    )}`;

    redirectToTenant(ele.division.tenant_id, redirectPath);
  };

  // Returns true when the project either does not require the Authorization & Agreement
  // or has an executed agreement; otherwise redirects the user to the project inspections
  // page and returns false. Failures fall open so transient API issues do not block start.
  const checkAgreementBeforeStart = async (ele) => {
    try {
      const agreementData = await authorizationAgreementApi.getAgreement(ele.projectId);
      if (agreementData.required && (!agreementData.agreement || agreementData.agreement.status !== 'executed')) {
        toast.error('An Authorization & Agreement must be signed before starting this inspection. Please visit the project inspections page.');
        appDivisionContext.saveAppDivisionId(ele.division.id);
        redirectToTenant(
          ele.division.tenant_id,
          `/divisions/${ele.division.id}/projects/${ele.projectId}/inspections`,
        );
        return false;
      }
    } catch (err) {
      // Allow navigation on API error
    }
    return true;
  };

  const handleCheckboxChange = (project) => {
    setSelectedProjects((prevSelectedProjects) =>
      prevSelectedProjects.find(
        (selectedProject) => selectedProject.id === project.id,
      )
        ? prevSelectedProjects.filter(
          (selectedProject) => selectedProject.id !== project.id,
        )
        : [...prevSelectedProjects, project],
    );
  };

  const GetDirection = async (direction) => {

    const latLongError = []
    const addresses = selectedProjects.map((project) => {
      if (!isValidLatLong(project.address ? project.address : project)) {
        latLongError.push(project.name ? project.name : project.id);
      }

      return project.address ? project.address : project;
    });


    if (latLongError.length > 0) {
      toast.error(
        <>
          The following projects are missing valid latitude and longitude data, which is required to complete routing.
          <br />
          {latLongError.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </>
      );

      return;
    }


    if (!addresses.length) {
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      const startLat = position.coords.latitude;
      const startLong = position.coords.longitude;

      const params = {
        origin: `${startLat},${startLong}`,
        waypoints: addresses
          .map((add) => `${add.latitude},${add.longitude}`)
          .join('|'),
      };

      if (direction === 3) {

        mapApi.getOptimizedRoute(params).then((response) => {

          setOpenDirectionModal(false);

          const optimisedWaypoints = response.data.optimizedOrder.map((index) => addresses[index]);


          const optimizedWaypointString = optimisedWaypoints
            .map((loc) => `${loc.latitude},${loc.longitude}`)
            .join('|');


          const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${startLat},${startLong}&destination=${startLat},${startLong}&waypoints=${optimizedWaypointString}`;

          const popup = window.open(googleMapsUrl, '_blank');

          if (!popup) {
            alert(
              'Pop-up blocked! Redirecting to Google Maps in the same tab.',
            );
            window.location.href = googleMapsUrl;
          }


        }).catch((err => {
          console.error('Error fetching optimized route:', err);
          toast.error('Error fetching optimized route:', err);

        })
        )
      }

      else {
        addresses.unshift({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });

        params.waypoints = addresses
          .map((add) => `${add.latitude},${add.longitude}`)
          .join('|');

        mapApi.directions(params).then((response) => {
          if (response) {
            const optimizedWaypoints = response.data;

            setOpenDirectionModal(false);

            if (optimizedWaypoints.length === 1) {
              alert('No route found');

              return;
            }

            let optimizedLocations = optimizedWaypoints
              .map((index) => addresses[index])
              .slice(1);

            if (direction === 2) {
              optimizedLocations = optimizedLocations.reverse();
            }

            const optimizedWaypointString = optimizedLocations
              .slice(0, optimizedLocations.length - 1)
              .map((loc) => `${loc.latitude},${loc.longitude}`)
              .join('|');

            const destAddress1 =
              optimizedLocations[optimizedLocations.length - 1];
            const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${startLat},${startLong}&destination=${destAddress1.latitude},${destAddress1.longitude}&waypoints=${optimizedWaypointString}`;

            const popup = window.open(googleMapsUrl, '_blank');

            if (!popup) {
              alert(
                'Pop-up blocked! Redirecting to Google Maps in the same tab.',
              );
              window.location.href = googleMapsUrl; // Fallback to same tab if pop-up is blocked
            }
          }
        });
      }
    } catch (err) {
      if (err.code === err.PERMISSION_DENIED) {
        alert(
          'You have denied location access. Please enable location services to proceed.',
        );
        alert(
          'To enable location, go to your browser settings or device settings.',
        );
      } else if (err.code === err.POSITION_UNAVAILABLE) {
        alert(
          "Location information is unavailable. Please check your device's connection.",
        );
      } else if (err.code === err.TIMEOUT) {
        alert('Location request timed out. Please try again.');
      } else {
        alert('An unknown error occurred while requesting location.');
      }
    }
  };

  const onDownload = async (projectId, inspectionTemplateId, allProjects) => {
    try {
      setDownloading(true);
      setProgress(0);
      setStatusText('Initializing download...');

      const inspections =
        allProjects &&
        allProjects.filter((item) => {
          return (
            projectId === item.projectId &&
            item.inspectionStatus === 'scheduled' &&
            format(new Date(), 'MM-dd-yyyy') === item.dueDate
          );
        });

      // Calculate total steps for better progress tracking
      const totalSteps = 15;

      let completedSteps = 0;

      const updateProgress = (stepIncrement = 1, message = null) => {
        completedSteps += stepIncrement;

        const newProgress = Math.min(
          Math.floor((completedSteps / totalSteps) * 100),
          95,
        );

        setProgress(newProgress);
        if (message) setStatusText(message);
      };

      // Step 1: Download project data
      setStatusText(steps[0] || 'Downloading project...');
      updateProgress(1);

      const response = await offlineApi.download(projectId, inspectionTemplateId).catch((error) => {
        console.error('Error downloading project:', error);
        throw new Error(error.message);
      });

      if (inspections && inspections.length > 0) {
        await inspectionApi
          .create(offlineProjectIds[0], 'Routine', 'sync offline')
          .catch((error) => {
            console.error('Error creating inspection:', error);
          });
      }

      updateProgress(2, steps[1] || 'Processing projects...');

      // Step 2: Process each project in sequence to avoid overwhelming the system
      for (const project of response.projects) {
        try {
          setStatusText(`Processing project ${project.name}...`);

          // Fetch client data
          await Promise.all([
            clientApi.show(project.client_id),
            clientApi.getCustomFields(project.client_id, [
              'finding_metric_tracking',
              'disable_images',
              'enable_photo_metadata_requirement',
            ]),
            projectApi.getDocs(project.id),
            permitApi.getProjectPermitStatus(project.id),
          ]);

          updateProgress(1);

          // Fetch and store site maps
          const uploads = await uploadApi.docTypes(
            'Active Site Maps',
            project.id,
          );

          const downloadPromises = [];

          if (uploads.dt && uploads.dt.length > 0) {
            const res = await uploadApi.project.index(project.id, uploads.dt.map((dt) => dt.id));

            for (const upload of res) {
              const url = getDownloadUrl(
                upload.upload.GUID,
                null,
                false,
                Date.now(),
                null,
                null,
                false,
              );

              await inspectionTemplateValidationApi.validate(
                projectId,
                upload.upload.GUID,
              );



              downloadPromises.push(
                downloadAndStoreImage(
                  url,
                  upload.upload.GUID,
                  upload.upload.name,
                  upload.upload.mime_type,
                  project.id,
                ),
              );
            }
          }

          // Wait for all image downloads to complete
          await Promise.allSettled(downloadPromises);
          updateProgress(1, steps[2] || 'Downloading images...');

          // Download and store legend items for the project's regulation group
          try {
            setStatusText(`Downloading legend items for project ${project.name}...`);

            // Fetch legend items by project - this gets both global and project-specific legend items
            const legendItems = await legendItemApi.indexByProject(
              project.id,
              appDivisionContext.appDivisionId
            ).catch((err) => {
              console.warn('Error fetching legend items:', err);
              return [];
            });

            // Store legend items in IndexedDB
            for (const legendItem of legendItems) {
              await db.legendItems.put({
                ...legendItem,
                project_id: project.id,
                regulation_group_id: project.document_group,
                sync: { synced: true, payload: [] }
              }).catch((err) => {
                console.error('Error storing legend item:', err);
              });
            }

            updateProgress(1, 'Legend items downloaded...');
          } catch (legendError) {
            console.error('Error downloading legend items:', legendError);
            // Continue even if legend items fail
          }

          // Download and store map data
          try {
            setStatusText(`Downloading map data for project ${project.name}...`);

            // Store map references for offline use
            for (const dt of uploads.dt || []) {
              const mapUploads = await uploadApi.project.index(project.id, dt.id);
              console.log(mapUploads,);

              // for (const mapUpload of mapUploads) {
              //   await db.maps.put({
              //     id: mapUpload.id,
              //     project_id: project.id,
              //     upload_id: mapUpload.upload.id,
              //     upload_guid: mapUpload.upload.GUID,
              //     name: mapUpload.upload.name,
              //     mime_type: mapUpload.upload.mime_type,
              //     url: getDownloadUrl(
              //       mapUpload.upload.GUID,
              //       null,
              //       false,
              //       Date.now(),
              //       null,
              //       null,
              //       false,
              //     ),
              //     updated_at: mapUpload.updatedAt || new Date().toISOString(),
              //     sync: { synced: true }
              //   }).catch((err) => {
              //     console.error('Error storing map data:', err);
              //   });
              // }
            }

            updateProgress(1, 'Map data downloaded...');
          } catch (mapError) {
            console.error('Error downloading map data:', mapError);
            // Continue even if map data fails
          }

          // Store project data
          await db.projects
            .put({ ...project, downloadedOn: new Date().toISOString() })
            .catch((err) => {
              console.error('Error storing project:', err);
            });

          updateProgress(1);
        } catch (projectError) {
          console.error(
            `Error processing project ${project.id}:`,
            projectError,
          );
          // Continue with next project instead of failing the entire process
        }

        // Step 3: Download inpspection template questions and signature

        const inspectionTemplate = await inspectionTemplateApi.get(
          inspectionTemplateId,
        );

        await fetchInspectionTemplateData(inspectionTemplateId);

        const questionGroups = await downloadInspectionTemplateData(
          inspectionTemplateId,
        );

        const signature = await signatureEditorApi.index(
          inspectionTemplateId,
        );

        // Step 3: Fetch question types
        const questionTypes = await questionTypeApi.index().catch((err) => {
          console.warn('Error fetching question types:', err);
        });

        await db.inspectionTemplate.put({
          template_id: inspectionTemplateId,
          questionTypes: questionTypes,
          template: {
            ...inspectionTemplate,
            question_groups: questionGroups,
          },
          signature: {
            attestations: signature.attestations,
            signatureGroups: signature.lines,
          },
        });
      }

      updateProgress(1, steps[3] || 'Processing inspections...');

      // Step 4: Process inspections for each project
      // for (const projectId of projectId) {
      try {
        const inspectionResponse = await projectInspectionApi.index(
          projectId,
          1,
          1,
        );

        if (
          !inspectionResponse.pageData ||
          inspectionResponse.pageData.length === 0
        ) {
          return;
        }

        for (const inspection of inspectionResponse.pageData) {
          try {
            // Store inspection data
            await db.inspections
              .put({ ...inspection, sync: { synced: true } })
              .catch((err) => {
                console.error('Error storing inspection:', err);
              });

            // Fetch project inspection data
            const uploads = await uploadApi.projectInspection.index(
              projectId,
              inspection.id,
            );

            await db.projectInspectionUploads
              .put({
                inspection_id: inspection.id,
                project_id: projectId,
                data: uploads,
                sync: { synced: true },
              })
              .catch((err) => {
                console.error(
                  'Error storing project inspection uploads:',
                  err,
                );
              });

            for (const upload of uploads) {
              const data = await positionableApi.index(
                'project_inspection_uploads',
                upload.id,
                inspection.id,
                'findings',
              );

              const data2 = await positionableApi.index(
                'project_uploads',
                upload.project_upload_id,
                inspection.id,
                'legend_items',
                'control_measure'
              );

              const data3 = await positionableApi.index(
                'project_uploads',
                upload.project_upload_id,
                inspection.id,
                'legend_items',
                'pollutant'
              );

              for (const item of data) {
                await db.positionables.put({
                  ...item,
                  project_id: projectId,
                  sync: { synced: true, payload: [] },
                });
              }

              for (const item of data2) {
                await db.positionables.put({
                  ...item,
                  project_id: projectId,
                  sync: { synced: true, payload: [] },
                });
              }

              for (const item of data3) {
                await db.positionables.put({
                  ...item,
                  project_id: projectId,
                  sync: { synced: true, payload: [] },
                });
              }


            }
            // fetch tenant backdating details
            await inspectionApi.getTenantBackdatingDetails();
            // fetch inspection attachments
            const inpsectionUploads =
              await inspectionApi.getInspectionUploads(inspection.id);

            await db.inspectionUploads
              .put({
                inspection_id: inspection.id,
                uploads: inpsectionUploads,
                sync: { synced: true },
              })
              .catch((err) => {
                console.error('Error storing inspection uploads:', err);
              });

            const downloadPromises = [];

            for (const upload of inpsectionUploads) {
              const url = getDownloadUrl(
                upload.upload.GUID,
                null,
                false,
                Date.now(),
                null,
                null,
                false,
              );

              downloadPromises.push(
                downloadAndStoreImage(
                  url,
                  upload.upload.GUID,
                  upload.upload.name,
                  upload.upload.mime_type,
                  response.projects[0].id,
                ),
              );
            }

            await Promise.allSettled(downloadPromises);

            // Process findings
            const findings = await findingApi.index(inspection.id);
            const findingImagePromises = [];

            for (const finding of findings) {
              finding.sync = {
                synced: true,
                payload: [],
              };

              await db.findings.put(finding).catch((err) => {
                console.error('Error storing finding:', err);
              });

              if (
                finding.finding_uploads &&
                finding.finding_uploads.length > 0
              ) {
                for (const findingUpload of finding.finding_uploads) {
                  const url = getDownloadUrl(
                    findingUpload.upload.GUID,
                    null,
                    false,
                    Date.now(),
                    null,
                    null,
                    false,
                  );

                  findingImagePromises.push(
                    downloadAndStoreImage(
                      url,
                      findingUpload.upload.GUID,
                      findingUpload.upload.name,
                      findingUpload.upload.mime_type,
                      response.projects[0].id,
                    ),
                  );
                }
              }
            }

            // Wait for all finding image downloads to complete
            await Promise.allSettled(findingImagePromises);
            updateProgress(1, steps[4] || 'Downloading inspection data...');

            // Process inspection questions
            const questionData = await inspectionQuestionApi.show(
              inspection.id,
            );
            const templateId = questionData.template.id;

            questionData.inspection_id = inspection.id;
            questionData.sync = { synced: true };

            // Fetch template data
            await inspectionTemplateApi.get(templateId);

            await fetchInspectionTemplateData(templateId);

            // Process condition questions
            if (questionData.template.question_groups) {
              const conditionGroup =
                questionData.template.question_groups.find(
                  (group) => group.name === 'Condition Group',
                );

              if (conditionGroup) {
                const conditionQuestions = conditionGroup.questions.filter(
                  (question) => question.input_label === 'Condition',
                );

                for (const question of conditionQuestions) {
                  await questionConfigValueApi.index(question.id);
                }
              }
            }

            // Store question data
            await db.questions.put(questionData).catch((err) => {
              console.error('Error storing questions:', err);
            });

            // Fetch and store certifications
            const certifications = await certificationApi.index(
              inspection.id,
            );

            await db.certifications
              .put({
                ...certifications,
                inspectionId: Number(certifications.inspectionId),
                sync: { synced: true, payload: [] },
              })
              .catch((err) => {
                console.error('Error storing certifications:', err);
              });

            // Fetch signed compliances
            await certificationApi.getAllSignedCompliancesWithCount(
              inspection.id,
            );

            // Fetch and store inspection notes
            const notes = await commentableApi.index(
              'inspections',
              inspection.id,
            );

            await db.inspectionNotes
              .put({
                inspection_id: inspection.id,
                notes: notes,
                sync: { synced: true, payload: [] },
              })
              .catch((err) => {
                console.error('Error storing inspection notes:', err);
              });

            updateProgress(1);
          } catch (inspectionError) {
            console.error(
              `Error processing inspection ${inspection.id}:`,
              inspectionError,
            );
            // Continue with next inspection
          }
        }
      } catch (projectInspectionError) {
        console.error(
          `Error fetching inspections for project ${projectId}:`,
          projectInspectionError,
        );
        // Continue with next project
      }
      // }

      // Complete the download process
      setProgress(100);
      setStatusText(steps[steps.length - 1] || 'Download complete!');
      toast.success('Download Complete to current device');
      setOfflineProjectsFunc();

      // Reset download state after a delay
      setTimeout(() => {
        setDownloading(false);
      }, 2000);
    } catch (error) {
      console.error('Download failed:', error);
      setStatusText(`Download failed: ${error.message}`);

      toast.error('Download failed: ' + error.message);

      // Reset download state after a delay
      setTimeout(() => {
        setDownloading(false);
      }, 3000);
    }
  };

  const removeOfflineProjects = async (projectId) => {
    removeOfflineProject(projectId)
      .then(() => {
        offlineApi.delete(projectId);
      }).then(() => {
        setOfflineProjectsFunc();
      }
      ).catch((e) => {
        console.log('error', e);
        toast.error('Error removing project');
      });
  };

  const syncOfflineProjects = async (projectId) => {
    try {
      setIsSyncing(true);
      setSyncStatus('Preparing to sync project...');
      setSyncProgress(0);

      const project = await db.projects.get(projectId);

      if (!project) throw new Error('Project not found');

      const unsyncedInspections = await db.inspections
        .where('project_id')
        .equals(projectId)
        .filter((inspection) => !inspection.sync.synced)
        .toArray();

      // Calculate total operations for progress tracking
      let totalOperations = unsyncedInspections.length;

      let completedOperations = 0;

      setSyncStatus(`Syncing project ${project.name}...`);


      // 1. Sync updated questions if inspection is already created/synced

      const inspections = await db.inspections
        .where('project_id')
        .equals(projectId)
        .filter((inspection) => inspection.sync.synced)
        .toArray();

      for (const inspection of inspections) {
        const question = await db.questions
          .where('inspection_id')
          .equals(inspection.id)
          .filter((q) => !q.sync?.synced)
          .first();

        if (question) {
          try {
            await inspectionApi.update(
              question.inspection_id,
              question.sync.payload,
            );
            await db.questions
              .where('inspection_id')
              .equals(question.inspection_id)
              .modify((f) => {
                f.sync.synced = true;
              });
          } catch (e) {
            console.error('Error syncing question', e);
            throw e;
          }
        }

        // 2. Sync Findings (can loop like above)

        const unsyncedFindings = await db.findings
          .where('id')
          .anyOf(inspection.findings.map((f) => f.id))
          .filter((finding) => !finding.sync.synced)
          .toArray();

        for (const finding of unsyncedFindings) {
          let findingCreated = null;

          for (const findingPayload of finding.sync.payload) {
            if (findingPayload.method === 'create') {
              findingPayload.images = [];

              try {
                findingCreated = await findingApi.create(
                  inspection.id,
                  findingPayload,
                );

                await db.findings
                  .where('id')
                  .equals(finding.id)
                  .modify((f) => {
                    f.sync.synced = true;
                  });
              } catch (e) {
                console.log('error', e);
              }
            } else if (findingPayload.method === 'update') {
              findingPayload.images = [];

              try {
                await findingApi.update(
                  findingCreated ? findingCreated.id : finding.id,
                  findingPayload,
                );

                await db.findings
                  .where('id')
                  .equals(finding.id)
                  .modify((f) => {
                    f.sync.synced = true;
                  });
              } catch (e) {
                console.log('error', e);
              }
            }
          }

          await db.positionables.where('start_inspection_id').equals(inspection.id).filter((positionable) => positionable.positionable_id === finding.id).modify((positionable) => {
            positionable.positionable_id = findingCreated ? findingCreated.id : finding.id;
            positionable.sync.payload = positionable.sync.payload.map((p) => {
              return {
                ...p,
                positionable_id: findingCreated ? findingCreated.id : finding.id,
              };
            })

          })


          const images = await db.images
            .where('payload_id')
            .equals(finding.id)
            .filter((upload) => !upload.sync.synced)
            .toArray();
          const uploads = finding.finding_uploads
            ? finding.finding_uploads.map((upload) => upload)
            : [];

          const newFiles = images
            ? images.map((image) => image.sync.payload.file)
            : [];

          if (newFiles.length > 0) {
            await uploadApi.finding.update(
              findingCreated ? findingCreated.id : finding.id,
              newFiles,
              uploads,
            );

            await db.images
              .where('payload_id')
              .equals(finding.id)
              .modify((img) => {
                img.sync.synced = true;
              });
          }
        }

        //get inspection uploads

        const unsyncedInspectionUploads = await db.images
          .where('payload_id')
          .equals(inspection.id)
          .filter((upload) => !upload.sync.synced)
          .toArray();

        for (const upload of unsyncedInspectionUploads) {
          const uploadResp = await uploadApi.create(upload.sync.payload.file);

          await uploadApi.inspection.create(uploadResp.id, inspection.id);

          await db.images
            .where('guid')
            .equals(upload.guid)
            .modify((u) => {
              u.sync.synced = true;
            });
        }

        //positionables
        const unsyncedPositionables = await db.positionables
          .where('start_inspection_id')
          .equals(inspection.id)
          .filter((positionable) => !positionable.sync.synced)
          .toArray();

        if (unsyncedPositionables.length > 0) {
          setSyncStatus(
            `Syncing ${unsyncedPositionables.length} positionables...`,
          );
          setSyncProgress(40);

          for (const positionable of unsyncedPositionables) {
            await positionableApi.bulkUpdate(
              positionable.target_type,
              positionable.target_id,
              positionable.sync.payload,
            );
          }

          await db.positionables
            .where('start_inspection_id')
            .equals(inspection.id)
            .modify((positionable) => {
              positionable.sync.synced = true;
            });
        }

        // 4. Sync certifications (can loop like above)
        const certification = await db.certifications
          .where('inspectionId')
          .equals(inspection.id)
          .filter((cert) => !cert.sync?.synced)
          .first();

        if (certification) {
          for (const payload of certification.sync.payload || []) {
            try {
              await certificationApi.create({
                inspectionId: certification.inspectionId,
                ...payload,
              });
            } catch (e) {
              console.error('Cert sync error', e);
            }
          }

          await db.certifications
            .where('inspectionId')
            .equals(certification.inspectionId)
            .modify((cert) => {
              cert.sync.synced = true;
            });
        }


        // inspection certification notes

        const unsyncedCertificationNotes = await db.inspectionNotes.get(
          inspection.id,
        );

        if (
          unsyncedCertificationNotes &&
          unsyncedCertificationNotes.sync.payload.length > 0 &&
          !unsyncedCertificationNotes.sync.synced
        ) {
          const noteIdsMap = new Map();

          for (const note of unsyncedCertificationNotes.sync.payload) {
            if (note.method === 'create') {
              const newNote = await commentableApi.create(
                'inspections',
                inspection.id,
                {
                  note: { content: note.content },
                },
              );

              noteIdsMap.set(note.id, newNote.id);
            }

            if (note.method === 'update') {
              await commentableApi.update(
                'inspections',
                note.id,
                {
                  content: note.content,
                  id: noteIdsMap.get(note.id) || note.id,
                },
                inspection.id,
              );
            }
          }

          await db.inspectionNotes
            .where('inspection_id')
            .equals(inspection.id)
            .modify((note) => {
              note.sync.synced = true;
            });
        }


        // inspection question comments
        const unsyncedInspectionQuestionComment = await db.commentPayload
          .where('inspection_id')
          .equals(inspection.id)
          .filter((comment) => !comment.sync.synced)
          .toArray();

        if (unsyncedInspectionQuestionComment.length > 0) {
          setSyncStatus(
            `Syncing ${unsyncedInspectionQuestionComment.length} inspection question comments...`,
          );

          for (const comment of unsyncedInspectionQuestionComment) {
            await inspectionApi.createQuestionComment(
              comment.question_id,
              inspection.id,
              comment.sync.payload,
            );
          }
          await db.commentPayload
            .where('inspection_id')
            .equals(inspection.id)
            .modify((c) => {
              c.sync.synced = true;
            });
        }
      }


      for (const inspection of unsyncedInspections) {
        const { sync } = inspection;
        const { payload } = sync;

        setSyncStatus(
          `Syncing inspection ${completedOperations + 1}/${unsyncedInspections.length
          }`,
        );

        await inspectionApi
          .create(payload.projectId, payload.type, 'offline')
          // eslint-disable-next-line no-loop-func
          .then(async (i) => {
            const newInspection = {
              ...inspection,
              id: i.id,
              sync: { synced: true },
            };

            await db.inspections
              .where('id')
              .equals(inspection.id)
              .modify(newInspection);

            const unsyncedQuestion = await db.questions
              .where('inspection_id')
              .equals(inspection.id)
              .filter((question) => !question.sync.synced)
              .first();

            if (unsyncedQuestion) {
              setSyncStatus(
                `Syncing questions for inspection ${completedOperations + 1
                }...`,
              );

              setSyncProgress(10);

              await inspectionApi
                .update(i.id, unsyncedQuestion.sync.payload)
                .then(() => {
                  db.questions
                    .where('inspection_id')
                    .equals(inspection.id)
                    .modify((question) => {
                      question.sync.synced = true;
                      question.inspection_id = i.id;
                    });
                })
                .catch((e) => {
                  console.log('error', e);
                });
            }

            const unsyncedFindings = await db.findings
              .where('id')
              .anyOf(inspection.findings.map((f) => f.id))
              .filter((finding) => !finding.sync.synced)
              .toArray();

            if (unsyncedFindings.length > 0) {
              setSyncStatus(
                `Syncing ${unsyncedFindings.length} findings for inspection ${completedOperations + 1
                }...`,
              );
            }
            for (const finding of unsyncedFindings) {
              let findingCreated = null;

              for (const findingPayload of finding.sync.payload) {
                if (findingPayload.method === 'create') {
                  findingPayload.images = [];

                  try {
                    findingCreated = await findingApi.create(i.id, {
                      ...findingPayload,
                      inspectionId: i.id,
                    });
                  } catch (e) {
                    console.log('error', e);
                  }
                } else if (findingPayload.method === 'update') {
                  findingPayload.images = [];

                  try {
                    await findingApi.update(
                      findingCreated ? findingCreated.id : finding.id,
                      {
                        ...findingPayload,
                        inspectionId: i.id,
                        id: findingCreated ? findingCreated.id : finding.id,
                      },
                    );
                  } catch (e) {
                    console.log('error', e);
                  }
                }
              }

              await db.findings
                .where('id')
                .equals(finding.id)
                .modify((f) => {
                  f.sync.synced = true;
                  f.id = findingCreated ? findingCreated.id : finding.id;
                });

              await db.positionables.where('start_inspection_id').equals(inspection.id).filter((positionable) => positionable.positionable_id === finding.id).modify((positionable) => {
                positionable.positionable_id = findingCreated ? findingCreated.id : finding.id;
                positionable.sync.payload = positionable.sync.payload.map((p) => {
                  return {
                    ...p,
                    start_inspection_id: i.id,
                    positionable_id: findingCreated ? findingCreated.id : finding.id,
                  };
                })

              })

              const images = await db.images
                .where('payload_id')
                .equals(finding.id)
                .filter((upload) => !upload.sync.synced)
                .toArray();

              const uploads = finding.finding_uploads
                ? finding.finding_uploads.map((upload) => upload)
                : [];

              const newFiles =
                images.map((image) => image.sync.payload.file) || [];

              setSyncProgress(20);

              if (newFiles.length > 0) {
                setSyncStatus(
                  `Uploading ${newFiles.length} images for finding...`,
                );

                await uploadApi.finding.update(
                  findingCreated ? findingCreated.id : finding.id,
                  newFiles,
                  uploads,
                );

                await db.images
                  .where('payload_id')
                  .equals(finding.id)
                  .modify((img) => {
                    img.sync.synced = true;
                  });
              }
            }


            //get inspection uploads

            const unsyncedInspectionUploads = await db.images
              .where('payload_id')
              .equals(inspection.id)
              .filter((upload) => !upload.sync.synced)
              .toArray();

            if (unsyncedInspectionUploads.length > 0) {
              setSyncStatus(
                `Uploading ${unsyncedInspectionUploads.length} inspection attachments...`,
              );
              setSyncProgress(30);
            }

            for (const upload of unsyncedInspectionUploads) {
              const uploadResp = await uploadApi.create(
                upload.sync.payload.file,
              );

              await uploadApi.inspection.create(uploadResp.id, i.id);

              await db.images
                .where('guid')
                .equals(upload.guid)
                .modify((u) => {
                  u.sync.synced = true;
                });
            }

            //positionables
            const unsyncedPositionables = await db.positionables
              .where('start_inspection_id')
              .equals(inspection.id)
              .filter((positionable) => !positionable.sync.synced)
              .toArray();

            if (unsyncedPositionables.length > 0) {
              setSyncStatus(
                `Syncing ${unsyncedPositionables.length} positionables...`,
              );
              setSyncProgress(40);

              for (const positionable of unsyncedPositionables) {
                positionable.sync.payload = positionable.sync.payload.map(
                  (p) => {
                    return {
                      ...p,
                      start_inspection_id: i.id,
                    };
                  },
                );

                await positionableApi.bulkUpdate(
                  positionable.target_type,
                  positionable.target_id,
                  positionable.sync.payload
                );
              }

              await db.positionables
                .where('start_inspection_id')
                .equals(inspection.id)
                .modify((positionable) => {
                  positionable.sync.synced = true;
                  positionable.start_inspection_id = i.id;
                });
            }

            const unsyncedCertification = await db.certifications
              .where('inspectionId')
              .equals(inspection.id)
              .filter((certification) => !certification.sync.synced)
              .first();

            if (unsyncedCertification && unsyncedCertification.sync.payload) {
              setSyncStatus(
                `Syncing certifications for inspection ${completedOperations + 1
                }...`,
              );
              setSyncProgress(50);

              for (const certPayload of unsyncedCertification.sync.payload) {
                try {
                  await certificationApi.create({
                    inspectionId: i.id,
                    group: certPayload.group,
                    ...certPayload,
                  });

                  await db.certifications
                    .where('inspectionId')
                    .equals(inspection.id)
                    .modify((certification) => {
                      certification.sync.synced = true;
                      certification.inspectionId = i.id;
                    });
                } catch (e) {
                  console.log('error', e);
                }
              }
            }

            // inspection certification notes

            const unsyncedCertificationNotes = await db.inspectionNotes.get(
              inspection.id,
            );

            if (
              unsyncedCertificationNotes &&
              unsyncedCertificationNotes.sync.payload.length > 0 &&
              !unsyncedCertificationNotes.sync.synced
            ) {
              const noteIdsMap = new Map();

              for (const note of unsyncedCertificationNotes.sync.payload) {
                if (note.method === 'create') {
                  const newNote = await commentableApi.create(
                    'inspections',
                    i.id,
                    {
                      note: { content: note.content },
                    },
                  );

                  noteIdsMap.set(note.id, newNote.id);
                }

                if (note.method === 'update') {
                  await commentableApi.update(
                    'inspections',
                    note.id,
                    {
                      content: note.content,
                      id: noteIdsMap.get(note.id) || note.id,
                    },
                    i.id,
                  );
                }
              }

              await db.inspectionNotes
                .where('inspection_id')
                .equals(inspection.id)
                .modify((certification) => {
                  certification.sync.synced = true;
                  certification.inspection_id = i.id;
                });
            }

            // inspection question comments
            const unsyncedInspectionQuestionComment = await db.commentPayload
              .where('inspection_id')
              .equals(inspection.id)
              .filter((comment) => !comment.sync.synced)
              .toArray();

            if (unsyncedInspectionQuestionComment.length > 0) {
              setSyncStatus(
                `Syncing ${unsyncedInspectionQuestionComment.length} inspection question comments...`,
              );

              for (const comment of unsyncedInspectionQuestionComment) {
                await inspectionApi.createQuestionComment(
                  comment.question_id,
                  i.id,
                  comment.sync.payload,
                );
              }
              await db.commentPayload
                .where('inspection_id')
                .equals(inspection.id)
                .modify((c) => {
                  c.sync.synced = true;
                  c.inspection_id = i.id;
                });
            }

            // Update progress after each inspection is processed
            completedOperations++;
            setSyncProgress((completedOperations / totalOperations) * 100);
          })
          .catch((e) => {
            console.log('error', e);
          });
      }

      await removeOfflineProjects(projectId);
      setSyncProgress(100);
      setSyncStatus('Project synced successfully');
      setIsSyncing(false);
      toast.success('Project synced successfully');
    } catch (e) {
      console.log('error', e);

      toast.error('Error syncing project: ' + e.message);
      setIsSyncing(false);

      throw e;
    }
  };

  const showIcons = useCallback((projectId, allProjects, inspection) => {
    const isOfflineDownloaded = offlineProjects.find(
      (project) => project.id === projectId,
    );

    if (navigator.onLine === false) {
      return <></>;
    }

    const ctaText = getCtaText(inspection.dueDate, inspection.inspectionStatus);

    if (isOfflineDownloaded && !inspection.external_project && (['Start Inspection', 'Past Due', 'Finish'].includes(ctaText))) {
      return (
        <>
          <FontAwesomeIcon
            icon={faTrash}
            color='red'
            cursor='pointer'
            style={{ margin: '5px', }}
            onClick={() => {
              setShowRemoveOfflineDownloadModal(true)
              setOfflineProjectId(projectId)
            }}
          />
          <FontAwesomeIcon
            icon={faUpload}
            color='green'
            cursor='pointer'
            style={{ margin: '5px' }}
            onClick={() => syncOfflineProjects(projectId)}
          />
        </>
      );
    } else if (inspection && !inspection.external_project && (['Start Inspection', 'Past Due', 'Finish'].includes(ctaText))) {
      return (
        <>
          <FontAwesomeIcon
            icon={faDownload}
            color='blue'
            cursor='pointer'
            style={{ margin: '5px' }}
            onClick={() => onDownload(projectId, inspection.inspection_template_id, allProjects)}
          />
        </>
      );
    }
    else {
      return <>
      </>

    }
  }, [offlineProjects, isOffline]);

  return (
    <>
      <div className='inspections-due' ref={widgetRef}>
        <header
          className='inspections-due__header'
          style={widgetWidth > 700 ? oneColumnHeader : null}
        >
          <h2 className='inspections-due__title'>My Projects</h2>
          <div className='inspections-due__bars'>
            {/* <section style={{ display: isCalendar ? "none" : "flex" }}>{appContext.state.user.name}</section> */}
            <input
              //  style={{ flexBasis: isCalendar ? "100%" : "60%" }}
              onChange={(event) => setSearch(event.target.value)}
              placeholder='Search'
            />
          </div>
          <div className='inspections-due__direction'>
            {selectedProjects.length > 0 && (
              <>
                <button
                  className='primary'
                  onClick={() => setOpenDirectionModal(true)}
                >
                  Directions
                </button>
              </>
            )}
          </div>
          <div className='inspections-due__ellipsis-button'>
            <span onClick={(event) => setAnchorEl(event.currentTarget)}>
              <FontAwesomeIcon icon={faEllipsisV} />
            </span>
          </div>
          <EllipsisPopover
            handleCloseFunc={handleClose}
            id={id}
            open={open}
            anchorEl={anchorEl}
            thirdOption={{
              sx: { p: 1 },
              icon: faList,
              optionName: 'Project List',
              handleOnClickFunc: handleProjectListView,
            }}
            forthOption={{
              sx: { p: 1 },
              icon: viewIcon,
              optionName: viewName,
              handleOnClickFunc: handleChangeView,
            }}
          />
        </header>

        <div
          data-rbd-drag-handle-context-id={providedProps}
          data-rbd-drag-handle-draggable-id='gibberish'
          style={{
            // When you set the data-rbd-drag-handle-context-id, RBD applies cursor: grab, so we need to revert that
            cursor: 'auto',
          }}
        >
          <div className="inspections-due__body">
            <div className="inspections-due__content">

              {localStorage.getItem('myProjectsWidgetPreference') === 'InspectionsDue' ?
                // ------------------------ Inspection Due List View ------------------------------------------------------------------- 
                (<section className="inspection-due__list-view">
                  <div className="inspections-due__table-head">
                    <h3 onClick={sortBySiteName}>Site Name <span><FontAwesomeIcon icon={faSort} /></span></h3>
                    <h3 onClick={sortByClientName}>Client Name <span><FontAwesomeIcon icon={faSort} /></span></h3>
                    <h3 onClick={sortByDueDate}>Due Date <span><FontAwesomeIcon icon={faSort} /></span> </h3>
                    <h3>Action</h3>
                  </div>

                  {!isLoadingForID ?
                    (displayStatsForID && displayStatsForID.map((ele, index) => {
                      const { siteName, clientName, dueDate, projectId, inspectionStatus } = ele;

                      const formattedTodayDate = new Date();
                      const formattedDueDate = dueDate ? parseDueDateLocal(dueDate) : null;

                      let startEvpInspection = false;

                      if (formattedDueDate && formattedTodayDate.setHours(0, 0, 0, 0) === formattedDueDate.getTime() && inspectionStatus === "scheduled") {
                        startEvpInspection = true;
                      }

                      return (
                        <div key={index} className="inspections-due__table-body">
                          {tenantFeatureContext.tenantHasFeature('Enable Route Planner') ?
                            <input
                              type="checkbox"
                              defaultChecked={selectedProjects.find(selectedProject => selectedProject.id === ele.projectId && selectedProject.inspectionId === ele.inspectionId)}
                              onChange={() => handleCheckboxChange({ id: ele.projectId, latitude: ele.latitude, longitude: ele.longitude, inspectionId: ele.inspectionId, name: ele.siteName })}
                              disabled={
                                selectedProjects.find(selectedProject => selectedProject.id === ele.projectId && selectedProject.inspectionId !== ele.inspectionId) ||
                                (selectedProjects.length === 10 && !selectedProjects.find(selectedProject => selectedProject.id === ele.projectId))
                              }
                            /> : <text></text>}
                          <li>{siteName}</li>
                          <li>{clientName}</li>
                          <li>{dueDate}</li>

                          {ele.division &&
                            ele.division.tenant_id === user.tenantId ? (
                            <>
                              <li>
                                {ele.external_project &&
                                  (inspectionStatus == 'new' ||
                                    startEvpInspection) ? (
                                  <span
                                    className={`list-view-submit-btn`}
                                    onClick={() =>
                                      handleEPVInspShowModal(
                                        ele,
                                        startEvpInspection,
                                      )
                                    }
                                  >
                                    Submit
                                  </span>
                                ) : getCtaText(dueDate, inspectionStatus) === 'Start Inspection' ? (
                                  (() => {
                                    const inspKey = `${ele.projectId}_${ele.inspectionId}`;
                                    const isStarting = startingInspectionKey === inspKey;

                                    return (
                                      <span
                                        className={classNames(
                                          handleButtonColors(dueDate, inspectionStatus),
                                          isStarting && 'disabled-btn',
                                        )}
                                        onClick={async () => {
                                          if (isStarting) return;
                                          setStartingInspectionKey(inspKey);
                                          const okToStart = await checkAgreementBeforeStart(ele);
                                          if (!okToStart) {
                                            setStartingInspectionKey(null);
                                            return;
                                          }
                                          if (inspectionStatus === 'new' && ele.inspectionId > 0) {
                                            appDivisionContext.saveAppDivisionId(ele.division.id);
                                            window.location.href = `/inspection/${ele.inspectionId}/questionnaire`;
                                          } else if (ele.junctionId > 0 || ele.project_inspection_template_id > 0) {
                                            handleStartInspection(ele).finally(() => setStartingInspectionKey(null));
                                          } else {
                                            handleCalendarStartInspection(ele).finally(() => setStartingInspectionKey(null));
                                          }
                                        }}
                                        style={{ cursor: isStarting ? 'not-allowed' : 'pointer', opacity: isStarting ? 0.6 : 1 }}
                                      >
                                        {isStarting ? 'Loading...' : (inspectionStatus === 'new' && ele.inspectionId > 0 ? 'Open Inspection' : getCtaText(dueDate, inspectionStatus))}
                                      </span>
                                    );
                                  })()
                                ) : (
                                  <Link
                                    to={getCtaRoute(
                                      inspectionStatus,
                                      dueDate,
                                      projectId,
                                      ele.inspectionId,
                                    )}
                                  >
                                    <span
                                      className={classNames(
                                        handleButtonColors(
                                          dueDate,
                                          inspectionStatus,
                                        ),
                                      )}
                                      onClick={() => appDivisionContext.saveAppDivisionId(ele.division.id)}
                                    >
                                      {getCtaText(dueDate, inspectionStatus)}
                                    </span>
                                  </Link>
                                )}
                                {tenantFeatureContext.tenantHasFeature('Enable Offline Mode') &&
                                  <span style={{ width: '60px' }} >
                                    {showIcons(projectId, displayStatsForID, ele)}
                                  </span>
                                }
                              </li>

                            </>
                          ) : (
                            <>
                              <li>
                                <span
                                  className={classNames(
                                    handleButtonColors(dueDate, inspectionStatus),
                                  )}
                                  onClick={() => handleRedirect(ele)}
                                >
                                  {getCtaText(dueDate, inspectionStatus)}
                                </span>
                                {tenantFeatureContext.tenantHasFeature('Enable Offline Mode') &&
                                  <span style={{ width: '60px' }}>   </span>
                                }
                              </li>

                            </>
                          )}
                        </div>
                      );
                    })
                    ) : (
                      <Loading what='data' />
                    )}
                </section>
                ) : localStorage.getItem('myProjectsWidgetPreference') ===
                  'ProjectList' ? (
                  // ------------------------ Project List View -------------------------------------------------------------------
                  <section className='inspection-due__calendar-view'>
                    <ProjectList
                      setSelectedProjects={setSelectedProjects}
                      selectedProjects={selectedProjects}
                    />
                  </section>
                ) : (
                  // ------------------------ Inspection Due Calendar View -------------------------------------------------------------------
                  <section className='inspection-due__calendar-view'>
                    <CalendarView
                      dueProjects={displayStatsForID}
                      getCtaRoute={getCtaRoute}
                      getCtaText={getCtaText}
                      onStartInspection={(ele) => {
                        if (ele.inspectionStatus === 'new' && ele.inspectionId > 0) {
                          appDivisionContext.saveAppDivisionId(ele.division.id);
                          window.location.href = `/inspection/${ele.inspectionId}/questionnaire`;
                        } else if (ele.junctionId > 0 || ele.project_inspection_template_id > 0) {
                          handleStartInspection(ele);
                        } else {
                          handleCalendarStartInspection(ele);
                        }
                      }}
                    />
                  </section>
                )}
            </div>

            {showExternalProjectInspectionModal ? (
              <ExternalProjectsInspectionSubmitModal
                handleClose={() => setShowExternalProjectInspectionModal(false)}
                handleSubmit={handleEPVInspSubmit}
                modalInspection={modalInspection}
                title='Submit EVP Inspection'
                inspectionTypes={inspectionTypes}
                project_name={modalInspection.siteName}
              />
            ) : (
              <></>
            )}

            <footer className='inspections-due__footer'>
              <span></span>
              <span onClick={changeShowMore}>
                Show {mdSize === 6 ? 'More' : 'Less'}{' '}
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </span>
              {selectedProjects.length ? (
                <span>
                  {' '}
                  Selected {selectedProjects.length} projects{' '}
                  {selectedProjects.length === 10 && '(max)'}{' '}
                </span>
              ) : (
                <span></span>
              )}
            </footer>
          </div>
        </div>
      </div>
      {openDirectionModal && (
        <FormContextProvider>
          <DirectionModal
            Submit={(direction) => GetDirection(direction)}
            onClose={() => setOpenDirectionModal(false)}
          />
        </FormContextProvider>
      )}
      <ConfirmationModal
        show={showRemoveOfflineDownloadModal}
        handleConfirm={() => {
          removeOfflineProjects(offlineProjectId);
          setShowRemoveOfflineDownloadModal(false);
          setOfflineProjectId(null);
          toast.success('Offline download removed successfully');
        }
        }
        handleClose={() => setShowRemoveOfflineDownloadModal(false)}
        title='Are you sure you want to remove offline download?'
        subTitle={`You're about to remove this downloaded project. This will permanently delete all associated work performed offline and cannot be undone. Are you sure you want to continue?`}
        buttonText='Yes, I understand'
        errorMsg={'Error removing offline download.'}
      />

      {downloading && (
        <Paper
          elevation={4}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            padding: 2,
            width: 250,
          }}
        >
          <Typography variant='body2' sx={{ marginBottom: 1 }}>
            {statusText}
          </Typography>
          <LinearProgress
            variant='determinate'
            value={progress}
            sx={{ marginBottom: 1 }}
          />
          <Typography variant='body2'>{progress}% completed</Typography>
        </Paper>
      )}
      {
        isSyncing && (
          <Paper
            elevation={4}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              padding: 2,
              width: 250,
            }}
          >
            <Typography variant='body2' sx={{ marginBottom: 1 }}>
              {syncStatus}
            </Typography>
            <LinearProgress
              variant='determinate'
              value={syncProgress}
              sx={{ marginBottom: 1 }}
            />
            <Typography variant='body2'>{syncProgress}% completed</Typography>
          </Paper>
        )

      }

      {mfaData && (
        <MFAVerificationModal
          show={showMFAModal}
          handleClose={handleMFAModalClose}
          onVerificationSuccess={handleMFAVerificationSuccess}
          email={mfaData.email}
          sendVerificationCode={authStore.sendVerificationCode}
          verifyCode={authStore.verifyCode}
        />
      )}
    </>
  );
};

export default InspectionsDue;
